import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import { theme } from '@Guidelines/Theme/theme';
import ConfirmVerifiedDocumentEditDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ConfirmationDialogs/ConfirmVerifiedDocumentEditDialog';
import RemoveListItemDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ConfirmationDialogs/RemoveListItemDialog';
import DOCUMENT_PANEL_WIDTH from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/Helpers/documentPanelWidth';
import requestCloseDocumentPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/requestCloseDocumentPanel';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import ComputedFieldPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/ComputedFieldPanel';
import ConditionPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/ConditionPanel';
import EmptyListPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/EmptyListPanel';
import FieldPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel';
import GonePanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/GonePanel';
import useListActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/Hooks/useListActions';
import ListItemPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/ListItemPanel';
import PanelContextTrail from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/PanelContextTrail';
import documentPanelSignal, {
	type DocumentPanelTarget,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import { css } from '@emotion/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useSelector } from '@verclam/synapse';
import { Button, Sidebar } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const TITLE_BY_TYPE = Object.freeze({
	field: 'Champ',
	condition: 'Valeurs liées',
	'list-item': 'Élément de liste',
	'empty-list': 'Liste',
	computed: 'Champ calculé',
	gone: 'Information',
} satisfies Record<DocumentPanelTarget['type'], string>);

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const navStyle = css`
	display: flex;
	gap: 0.375rem;
`;

const footerNavStyle = css`
	display: flex;
	gap: 0.75rem;
	width: 100%;

	& > * {
		flex: 1;
	}
`;

const sidebarStyle = css`
	@media (min-width: ${theme.layout.compactBreakpoint}) {
		width: ${DOCUMENT_PANEL_WIDTH};
	}
`;

/**
 * The sidebar of the document mode: one panel per kind of object clicked in the document (field,
 * conditional text, list element, empty list, computed field) and the one for an object that has
 * left the document. A non-modal panel at the right on desktop, full screen on the phone where ‹ › sit in
 * the footer; the close cross, Échap and ‹ › all go through the draft guard. Also owns the list gestures
 * (`useListActions`), shared by the element panel and the viewer's bars, and their confirmations.
 *
 * The last open target is kept while the panel slides out, so its content does not blank mid-animation.
 */
const DocumentPanelSidebar: React.FC = () => {
	const target = useSignalValue(documentPanelSignal);
	const isCompact = useIsCompactViewport();
	const document = useSelector((state) => state.documentsStore.document);
	const { goToAdjacentPending, nextPendingField } = useDocumentViewer();
	const { requestListAction, isBusy, confirmation, acceptConfirmation, dismissConfirmation } =
		useListActions();

	const [displayed, setDisplayed] = React.useState<DocumentPanelTarget | null>(null);

	if (target !== null && target !== displayed) {
		setDisplayed(target);
	}

	const handleHide = React.useCallback(() => {
		requestCloseDocumentPanel();
	}, []);

	const handlePrevious = React.useCallback(() => {
		goToAdjacentPending('previous');
	}, [goToAdjacentPending]);

	const handleNext = React.useCallback(() => {
		goToAdjacentPending('next');
	}, [goToAdjacentPending]);

	const renderPanel = (panel: DocumentPanelTarget): React.ReactNode => {
		if (document === null) {
			return null;
		}

		switch (panel.type) {
			case 'field':
				if (document.findFieldByKey(panel.fieldKey) === null) {
					return <GonePanel reason="field" />;
				}

				return <FieldPanel fieldKey={panel.fieldKey} occurrenceKey={panel.occurrenceKey} />;
			case 'condition':
				return <ConditionPanel target={panel} />;
			case 'list-item': {
				const list = document.findListByKey(panel.listKey);

				if (list === null || list.items[panel.index] === undefined) {
					return <GonePanel reason="item" />;
				}

				return (
					<ListItemPanel
						list={list}
						index={panel.index}
						isBusy={isBusy}
						onAction={requestListAction}
					/>
				);
			}
			case 'empty-list': {
				const list = document.findListByKey(panel.listKey);

				if (list === null) {
					return <GonePanel reason="item" />;
				}

				return <EmptyListPanel list={list} isBusy={isBusy} onAction={requestListAction} />;
			}
			case 'computed':
				return <ComputedFieldPanel target={panel} />;
			case 'gone':
				return <GonePanel reason={panel.reason} />;
		}
	};

	return (
		<React.Fragment>
			<Sidebar
				visible={target !== null}
				onHide={handleHide}
				position="right"
				fullScreen={isCompact}
				modal={isCompact}
				dismissable={false}
				css={sidebarStyle}
				header={displayed === null ? undefined : TITLE_BY_TYPE[displayed.type]}
				icons={
					<ConditionalLoader data={!isCompact && nextPendingField !== null}>
						<div css={navStyle}>
							<Button
								type="button"
								icon={<ChevronLeft size={16} />}
								severity="secondary"
								outlined
								rounded
								aria-label="Champ à vérifier précédent"
								onClick={handlePrevious}
							/>
							<Button
								type="button"
								icon={<ChevronRight size={16} />}
								severity="secondary"
								outlined
								rounded
								aria-label="Champ à vérifier suivant"
								onClick={handleNext}
							/>
						</div>
					</ConditionalLoader>
				}
				footer={
					isCompact && nextPendingField !== null ? (
						<div css={footerNavStyle}>
							<Button
								type="button"
								icon={<ChevronLeft size={16} />}
								label="Précédent"
								severity="secondary"
								outlined
								onClick={handlePrevious}
							/>
							<Button
								type="button"
								icon={<ChevronRight size={16} />}
								iconPos="right"
								label="Suivant"
								severity="secondary"
								outlined
								onClick={handleNext}
							/>
						</div>
					) : undefined
				}
			>
				<ConditionalLoader data={displayed}>
					{(panel) => (
						<div css={bodyStyle}>
							<PanelContextTrail target={panel} />
							{renderPanel(panel)}
						</div>
					)}
				</ConditionalLoader>
			</Sidebar>
			<RemoveListItemDialog
				visible={confirmation?.type === 'remove'}
				itemName={confirmation?.type === 'remove' ? confirmation.itemName : ''}
				isDocumentVerified={confirmation?.type === 'remove' && confirmation.isDocumentVerified}
				onAccept={acceptConfirmation}
				onHide={dismissConfirmation}
			/>
			<ConfirmVerifiedDocumentEditDialog
				visible={confirmation?.type === 'verified'}
				onAccept={acceptConfirmation}
				onHide={dismissConfirmation}
			/>
		</React.Fragment>
	);
};

export default DocumentPanelSidebar;
